import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Clock, Trophy, TrendingUp, TrendingDown, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { API } from '../config/api';

const LapByLapComparison = ({ raceId, driverNumbers, getDriverColor }) => {
  const [driversData, setDriversData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (driverNumbers && driverNumbers.length > 0) {
      loadDriversLaps();
    }
  }, [raceId, driverNumbers]);

  const loadDriversLaps = async () => {
    setLoading(true);
    try {
      const promises = driverNumbers.map(num => 
        axios.get(`${API}/races/${raceId}/drivers/${num}/laps`)
      );
      const responses = await Promise.all(promises);
      setDriversData(responses.map(r => r.data).filter(d => d.lap_details && d.lap_details.length > 0));
    } catch (error) {
      toast.error('Error al cargar vueltas');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(3);
    return mins > 0 ? `${mins}:${secs.padStart(6, '0')}` : `${secs}s`;
  };

  const getChartData = () => {
    if (!driversData || driversData.length === 0) return [];
    
    const maxLaps = Math.max(...driversData.map(d => d.lap_details.length));
    const data = [];
    
    for (let lapNum = 1; lapNum <= maxLaps; lapNum++) {
      const point = { lap: lapNum };
      driversData.forEach(driver => {
        const lap = driver.lap_details.find(l => l.lap_number === lapNum);
        if (lap) {
          point[`#${driver.driver_number}`] = lap.lap_time;
        }
      });
      data.push(point);
    }
    
    return data;
  };

  // Position-by-lap chart data: one point per lap × driver. Y axis is
  // reversed elsewhere so P1 sits at the top; drivers who didn't have
  // a lap that far are simply omitted from that point (recharts skips
  // nulls). Backend serves `position` per lap via /drivers/{n}/laps
  // (see server.py::get_driver_laps).
  const positionChartData = useMemo(() => {
    if (!driversData || driversData.length === 0) return [];
    const maxLaps = Math.max(...driversData.map(d => d.lap_details.length));
    const data = [];
    for (let lapNum = 1; lapNum <= maxLaps; lapNum++) {
      const point = { lap: lapNum };
      driversData.forEach(driver => {
        const lap = driver.lap_details.find(l => l.lap_number === lapNum);
        if (lap && lap.position) point[`P#${driver.driver_number}`] = lap.position;
      });
      data.push(point);
    }
    return data;
  }, [driversData]);

  const maxPosition = useMemo(() => {
    let max = 1;
    for (const p of positionChartData) {
      for (const k of Object.keys(p)) {
        if (k.startsWith('P#') && p[k] > max) max = p[k];
      }
    }
    return max;
  }, [positionChartData]);

  const hasPositionData = positionChartData.some((p) =>
    Object.keys(p).some((k) => k.startsWith('P#'))
  );

  if (loading) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <div className="w-16 h-16 mx-auto border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-400">Cargando datos de vueltas...</p>
        </CardContent>
      </Card>
    );
  }

  if (!driversData || driversData.length === 0) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <Clock className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No hay datos de vueltas disponibles para estos pilotos</p>
          <p className="text-sm text-gray-500 mt-2">Los datos vuelta por vuelta solo están disponibles en archivos XLS</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-red-950/50 to-neutral-900 border-red-600">
        <CardHeader>
          <CardTitle className="text-2xl text-white flex items-center gap-2">
            <Clock className="w-7 h-7 text-red-500" />
            Comparación Vuelta por Vuelta
          </CardTitle>
          <CardDescription className="text-gray-400">
            {driversData.length} pilotos comparados
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Chart */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white">Evolución de Tiempos de Vuelta</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getChartData()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis 
                dataKey="lap" 
                stroke="#9ca3af" 
                label={{ value: 'Vuelta', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
              />
              <YAxis 
                stroke="#9ca3af" 
                label={{ value: 'Tiempo (s)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#171717',
                  border: '1px solid #7f1d1d',
                  borderRadius: '8px'
                }}
                labelStyle={{ color: '#ffffff' }}
                formatter={(value) => formatTime(value)}
              />
              <Legend />
              {driversData.map((driver, idx) => (
                <Line
                  key={driver.driver_number}
                  type="monotone"
                  dataKey={`#${driver.driver_number}`}
                  name={`#${driver.driver_number} ${driver.driver_name}`}
                  stroke={getDriverColor ? getDriverColor(idx) : '#ffffff'}
                  strokeWidth={3}
                  dot={{ fill: getDriverColor ? getDriverColor(idx) : '#ffffff', r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Position-by-lap chart — one line per driver, Y axis reversed
          (P1 at the top). Only rendered when at least one driver has
          position data available for at least one lap. */}
      {hasPositionData && (
        <Card className="bg-neutral-900 border-red-900/30" data-testid="position-chart-comparison">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-red-500" />
              Posición por Vuelta
            </CardTitle>
            <CardDescription className="text-gray-400">
              Ranking de cada piloto al final de cada vuelta (P1 arriba).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={positionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis
                  dataKey="lap"
                  stroke="#9ca3af"
                  label={{ value: 'Vuelta', position: 'insideBottom', offset: -5, fill: '#9ca3af' }}
                />
                <YAxis
                  stroke="#9ca3af"
                  domain={[1, Math.max(maxPosition, 2)]}
                  reversed
                  allowDecimals={false}
                  label={{ value: 'Posición', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#171717', border: '1px solid #7f1d1d', borderRadius: '8px' }}
                  labelStyle={{ color: '#ffffff' }}
                  formatter={(value) => [`P${value}`, 'Posición']}
                />
                <ReferenceLine y={1} stroke="#dc2626" strokeDasharray="4 4" label={{ value: 'P1', fill: '#dc2626', position: 'right' }} />
                <Legend />
                {driversData.map((driver, idx) => (
                  <Line
                    key={`pos-${driver.driver_number}`}
                    type="monotone"
                    dataKey={`P#${driver.driver_number}`}
                    name={`#${driver.driver_number} ${driver.driver_name}`}
                    stroke={getDriverColor ? getDriverColor(idx) : '#ffffff'}
                    strokeWidth={3}
                    dot={{ fill: getDriverColor ? getDriverColor(idx) : '#ffffff', r: 4 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">        {driversData.map((driver, idx) => (
          <Card
            key={driver.driver_number}
            className="bg-neutral-900 border-red-900/30"
            style={{ borderColor: getDriverColor ? getDriverColor(idx) : '#fff' }}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-white">
                    #{driver.driver_number}
                  </CardTitle>
                  <CardDescription className="text-gray-300">
                    {driver.driver_name}
                  </CardDescription>
                </div>
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getDriverColor ? getDriverColor(idx) : '#fff' }}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Vueltas</span>
                  <Badge className="bg-neutral-800 text-white">
                    {driver.lap_details.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Mejor</span>
                  <span className="text-white font-semibold">{formatTime(driver.best_lap)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Table */}
      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white">Tabla Comparativa por Vuelta</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-4 text-gray-300 font-semibold">Vuelta</th>
                  {driversData.map((driver, idx) => (
                    <th key={driver.driver_number} className="text-center py-3 px-4 text-gray-300 font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: getDriverColor ? getDriverColor(idx) : '#fff' }}
                        />
                        #{driver.driver_number}
                      </div>
                    </th>
                  ))}
                  <th className="text-center py-3 px-4 text-gray-300 font-semibold">Delta</th>
                </tr>
              </thead>
              <tbody>
                {getChartData().map((lapData) => {
                  const times = driversData.map((driver, idx) => ({
                    driver: driver.driver_number,
                    time: lapData[`#${driver.driver_number}`],
                    idx
                  })).filter(d => d.time);
                  
                  const fastestTime = times.length > 0 ? Math.min(...times.map(t => t.time)) : 0;
                  const fastestDriver = times.find(t => t.time === fastestTime);
                  
                  return (
                    <tr key={lapData.lap} className="border-b border-neutral-800 hover:bg-red-950/20">
                      <td className="py-3 px-4 font-medium text-white">{lapData.lap}</td>
                      {driversData.map((driver, idx) => {
                        const time = lapData[`#${driver.driver_number}`];
                        const isFastest = time && time === fastestTime;
                        
                        return (
                          <td key={driver.driver_number} className="text-center py-3 px-4">
                            {time ? (
                              <div className="flex items-center justify-center gap-2">
                                <span className={`font-mono ${isFastest ? 'text-white font-bold' : 'text-gray-300'}`}>
                                  {formatTime(time)}
                                </span>
                                {isFastest && <Trophy className="w-3 h-3 text-red-500" />}
                              </div>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center py-3 px-4">
                        {times.length >= 2 && (
                          <span className="font-mono text-sm text-red-400">
                            {(Math.max(...times.map(t => t.time)) - fastestTime).toFixed(3)}s
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LapByLapComparison;
